import React, { useState } from 'react'

import {
  colors,
  useUnsafeCallback,
  useUnsafeEffect,
  useUnsafeMemo,
} from '@factorialco/deprecated-design-system'
import {
  CartesianGrid,
  ComposedChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  TooltipContentProps,
  XAxis,
  YAxis,
} from 'recharts'

import { ComboBar } from './ComboBar'
import { ComboLine } from './ComboLine'
import { CustomTooltip } from './CustomTooltip'
import { Ticks } from './Ticks'
import { buildComboChartData, extractLineNullSeries } from './helpers'
import './index.styles.css'
import { BarPositions, ComboChartProps } from './types'

export const ComboChart: React.FC<ComboChartProps> = ({
  width,
  series,
  height,
  margin,
  onClick,
  chartData,
  formatTick,
  yAxisTickCount,
  barCategoryGap,
  tooltipContent,
  yAxisTickFormatter,
  showTooltipCursor = true,
}) => {
  const [tooltipIndex, setTooltipIndex] = useState<number>()
  const [barPositions, setBarPositions] = useState<BarPositions>()

  const { fullSeries, comboChartData } = useUnsafeMemo(() => {
    const newSeries = [...series]
    const fullChartData = [...chartData]

    newSeries.forEach(({ lines }) => {
      lines.forEach(({ id: lineId, color }) => {
        const lineData = chartData.find(({ id }) => id === lineId)
        extractLineNullSeries(lineData).forEach((nullChartdata) => {
          fullChartData.push(nullChartdata)
          lines.push({ id: nullChartdata.id, color })
        })
      })
    })

    return { fullSeries: newSeries, comboChartData: buildComboChartData(fullChartData) }
  }, [series, chartData])

  const hasGroups = useUnsafeMemo(() => {
    return series.filter(({ bars }) => bars.length > 0).length > 1
  }, [series])

  const hasBars = useUnsafeMemo(() => {
    return series.some(({ bars }) => {
      return bars.some(({ id: barId }) => {
        return chartData.some(({ id, data }) => {
          return id === barId && data.some(({ y }) => !!y)
        })
      })
    })
  }, [series, chartData])

  const hasLines = useUnsafeMemo(() => {
    return series.some(({ lines }) => {
      return lines.some(({ id: lineId }) => {
        return chartData.some(({ id, data }) => {
          return id === lineId && data.some(({ y }) => !!y)
        })
      })
    })
  }, [series, chartData])

  const renderTick = useUnsafeCallback(
    ({ y, index, value }) => {
      return (
        <Ticks
          y={y}
          index={index}
          value={value}
          series={series}
          chartData={chartData}
          formatTick={formatTick}
          barPositions={barPositions || {}}
        />
      )
    },
    [series, chartData, formatTick, barPositions]
  )

  const finalMargin = useUnsafeMemo(
    () => ({
      ...(margin || {}),
      bottom: hasGroups && hasBars ? (margin?.bottom || 0) + 30 : margin?.bottom || 0,
    }),
    [margin, hasBars, hasGroups]
  )

  useUnsafeEffect(() => {
    setBarPositions(undefined)
  }, [fullSeries])

  return (
    <ResponsiveContainer
      width={width}
      debounce={200}
      height={height}
      onResize={() => setBarPositions(undefined)}
    >
      <ComposedChart
        maxBarSize={40}
        margin={finalMargin}
        data={comboChartData}
        barCategoryGap={barCategoryGap}
      >
        <CartesianGrid
          strokeWidth={1}
          vertical={false}
          strokeDasharray="4 4"
          stroke={colors.grey300}
        />
        <YAxis
          tickLine={false}
          axisLine={false}
          allowDataOverflow
          tickCount={yAxisTickCount}
          tickFormatter={yAxisTickFormatter}
          domain={hasLines || hasBars ? undefined : [0, 100]}
        />
        <ReferenceLine y={0} stroke={colors.grey300} />
        <XAxis
          dataKey="x"
          tickMargin={13}
          tickLine={false}
          fill={colors.grey800}
          axisLine={{
            strokeWidth: 1,
            strokeDashoffset: 4,
            strokeDasharray: '4 4',
            stroke: colors.grey300,
          }}
          tickFormatter={formatTick}
          padding={{ left: 20, right: 20 }}
          tick={
            hasGroups && hasBars
              ? ({ x, y, index, payload: { value } }) => renderTick({ x, y, index, value })
              : undefined
          }
        />
        {tooltipContent ? (
          <Tooltip
            offset={60}
            cursor={showTooltipCursor}
            content={(tooltipProps: TooltipContentProps<string, number>) => (
              <CustomTooltip
                data={comboChartData}
                tooltipProps={tooltipProps}
                tooltipContent={tooltipContent}
                setTooltipIndex={setTooltipIndex}
              />
            )}
          />
        ) : null}
        {hasBars && (
          <ComboBar
            onClick={onClick}
            series={fullSeries}
            tooltipIndex={tooltipIndex}
            barPositions={barPositions}
            setBarPositions={setBarPositions}
          />
        )}
        {barPositions || !hasBars ? (
          <ComboLine onClick={onClick} series={fullSeries} barPositions={barPositions} />
        ) : null}
      </ComposedChart>
    </ResponsiveContainer>
  )
}
